import React from 'react'
import { Field } from 'formik'
import { Card, Columns, Element } from 'react-bulma-components'
import InputField from '../../../../form/fields/input'
import SelectField from '../../../../form/fields/select'
import AutocompleteField from '../../../../form/fields/autocomplete'
import { buildListAsInputOptions } from '../../../../../utils/forms'
import {
  useAssociatedOrganizationsQuery,
  useOrganizationQuery,
} from '../../../../../queries/organizations'
import { useServicesQuery } from '../../../../../queries/services'
import useStore from '../../../../../store'
import { Organization } from '../../../../../api/organizations'
import { WorkersRequestFormProps } from '../workers-request-form.types'

/**
 * Props of the `<MissionDataSection>` component.
 *
 * The section only needs to know about the current resource being edited (to
 * compute the `disabled` state of the fields) and the template being used (to
 * pre-fill the autocomplete input values).
 */
export interface MissionDataSectionProps {
  workersRequest?: WorkersRequestFormProps extends { workersRequest?: infer W } ? W : never
  fromTemplate?: WorkersRequestFormProps extends { fromTemplate?: infer T } ? T : never
}

/**
 * Top-level Card rendering the mission-data fields of the workers-request-form:
 * interim agency, service (affectation), address and complementary informations.
 *
 * The section owns its own data queries (organization, interim agencies, services).
 */
const MissionDataSection: React.FC<MissionDataSectionProps> = ({
  workersRequest,
  fromTemplate,
}) => {
  const currentOrganization = useStore(state => state.session.currentOrganization as Organization)
  const organizationQuery = useOrganizationQuery(currentOrganization._id, currentOrganization.type)
  const servicesQuery = useServicesQuery()
  const interimAgenciesQuery = useAssociatedOrganizationsQuery()

  return (
    <Card mb={6}>
      <Card.Content>
        <Columns>
          <Columns.Column size={6}>
            <Field
              label="Agence d'intérim concernée"
              name="interimAgency"
              component={SelectField}
              required
              fullwidth="true"
              options={buildListAsInputOptions(interimAgenciesQuery.data || [])}
              selectDefaultChooseOptionLabel="Agence d'Intérim..."
              disabled={Boolean(workersRequest)}
            />
          </Columns.Column>
          <Columns.Column size={6}>
            <Field
              key={workersRequest?.missionData?.service?._id}
              label="Affectation"
              name="missionData.service"
              component={AutocompleteField}
              placeholder="Affectation..."
              onlySelect
              required
              initialInputValue={
                fromTemplate?.mission?.service?.name ||
                fromTemplate?.workersRequest?.missionData?.service?.name ||
                workersRequest?.missionData?.service?.name
              }
              items={buildListAsInputOptions(servicesQuery.data || [])}
              fullwidth="true"
              selectDefaultChooseOptionLabel={"Choisir l'Affectation"}
              disabled={workersRequest && !workersRequest.__actions.canBeUpdated}
            />
          </Columns.Column>
        </Columns>
        <Columns>
          <Columns.Column>
            <Element>
              <Field
                key={workersRequest?.missionData?.address}
                name="missionData.address"
                label="Adresse de la prestation"
                component={AutocompleteField}
                required
                items={buildListAsInputOptions(organizationQuery.data?.addresses ?? [])}
                disabled={workersRequest && !workersRequest.__actions.canBeUpdated}
              />
            </Element>
          </Columns.Column>
        </Columns>
        <Columns>
          <Columns.Column>
            <Element>
              <Field
                name="missionData.complementaryInformations"
                label="Informations complémentaires pour l'Intérimaire"
                component={InputField}
                disabled={workersRequest}
              />
            </Element>
          </Columns.Column>
        </Columns>
      </Card.Content>
    </Card>
  )
}

export default MissionDataSection
